import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api, type WebhookEndpoint } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Skeleton,
  inputClass,
  relativeTime,
} from "../components/ui";

interface EventInfo {
  event: string;
  description: string;
}

export function Webhooks() {
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[] | null>(null);
  const [events, setEvents] = useState<EventInfo[]>([]);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, available] = await Promise.all([
        api.get<WebhookEndpoint[]>("/webhooks"),
        api.get<EventInfo[]>("/webhooks/events"),
      ]);
      setEndpoints(list);
      setEvents(available);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not load webhooks.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Webhooks</h1>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Push events to another system as they happen. Retried with backoff for up to six hours.
          </p>
        </div>
        {!creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add endpoint
          </Button>
        )}
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}

      {secret && (
        <Card className="animate-rise border-[var(--brand)]/40 bg-[var(--brand-soft)]">
          <div className="text-[15px] font-semibold text-[var(--brand)]">
            Signing secret — copy it now
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--muted)]">
            Every delivery carries{" "}
            <code className="font-mono text-[12px]">X-Sentryq-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code>, an
            HMAC-SHA256 over <code className="font-mono text-[12px]">&lt;t&gt;.&lt;raw body&gt;</code>. Verify
            against the raw bytes before parsing, and reject anything where t is over five minutes old.
          </p>
          <div className="mt-3 flex gap-2">
            <code className="flex-1 overflow-x-auto rounded-[10px] border border-[var(--border)] bg-[var(--background)] px-3 py-2.5 font-mono text-[13px]">
              {secret}
            </code>
            <Button onClick={() => void navigator.clipboard.writeText(secret)}>Copy</Button>
            <Button variant="ghost" onClick={() => setSecret(null)}>
              Done
            </Button>
          </div>
        </Card>
      )}

      {creating && (
        <CreateEndpoint
          events={events}
          onCancel={() => setCreating(false)}
          onCreated={(s) => {
            setSecret(s);
            setCreating(false);
            void load();
          }}
        />
      )}

      {endpoints === null ? (
        <div className="grid gap-3">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : endpoints.length === 0 ? (
        <Card padded={false}>
          <Empty
            title="No endpoints"
            body="Add one to have approvals, rejections and messages posted to your own system as they happen."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add endpoint
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {endpoints.map((e) => (
            <Card key={e.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-mono text-[13px]">{e.url}</span>
                    <Badge tone={e.isActive ? "ok" : "danger"}>{e.isActive ? "active" : "disabled"}</Badge>
                    {e.consecutiveFailures > 0 && (
                      <Badge tone="warn">{e.consecutiveFailures} failing</Badge>
                    )}
                  </div>
                  {e.description && (
                    <div className="mt-1 text-[13px] text-[var(--muted)]">{e.description}</div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {e.events.map((ev) => (
                      <Badge key={ev} tone="neutral">
                        {ev}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[var(--faint)]">
                    <span>last success {relativeTime(e.lastSuccessAt)}</span>
                    {e.lastFailureAt && (
                      <>
                        <span>·</span>
                        <span>last failure {relativeTime(e.lastFailureAt)}</span>
                      </>
                    )}
                  </div>
                  {e.disabledReason && (
                    <div className="mt-2.5 rounded-[10px] bg-[var(--surface-2)] px-3 py-2.5">
                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">
                        Why it stopped
                      </div>
                      <div className="mt-1 text-[13px] text-[var(--muted)]">{e.disabledReason}</div>
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button
                    onClick={() =>
                      void api.patch(`/webhooks/${e.id}`, { isActive: !e.isActive }).then(load)
                    }
                  >
                    {e.isActive ? "Disable" : "Enable"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!confirm(`Delete this endpoint? Queued deliveries for it will be dropped.`)) return;
                      void api.delete(`/webhooks/${e.id}`).then(load);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateEndpoint({
  events,
  onCancel,
  onCreated,
}: {
  events: EventInfo[];
  onCancel: () => void;
  onCreated: (secret: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ secret: string }>("/webhooks", {
        url,
        description,
        events: selected,
      });
      onCreated(result.secret);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not create the endpoint.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="animate-rise">
      <h2 className="text-[15px] font-semibold">Add an endpoint</h2>
      <div className="mt-4 grid gap-4">
        <Field
          label="URL"
          hint="Must be public https. Loopback and private addresses are refused."
        >
          <input
            className={inputClass}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.example.com/sentryq"
            autoFocus
          />
        </Field>
        <Field label="Description" hint="Optional. What is on the other end?">
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
          />
        </Field>

        <div>
          <div className="mb-2 text-[13px] font-medium">Events</div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {events.map((e) => (
              <label
                key={e.event}
                className="flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] p-2.5 transition-colors duration-150 hover:border-[var(--border-strong)]"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[var(--brand)]"
                  checked={selected.includes(e.event)}
                  onChange={(ev) =>
                    setSelected((prev) =>
                      ev.target.checked ? [...prev, e.event] : prev.filter((x) => x !== e.event),
                    )
                  }
                />
                <span className="min-w-0">
                  <span className="block font-mono text-[12px]">{e.event}</span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-[var(--faint)]">
                    {e.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => void create()}
            disabled={busy || !url.trim() || selected.length === 0}
          >
            {busy ? "Creating…" : "Create endpoint"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
